import React from "react";
import { EuiBadge, EuiIcon, EuiLink, EuiText, EuiToolTip } from "@elastic/eui";
import { OpsanioRefPipeline } from "src/questionnaires";
import { useAnketaI18n } from "src/internationalization";

const I18N = "anketa.opsanio.version";

/**
 * How a GitLab pipeline status maps onto an EUI badge. Anything unknown falls
 * back to a neutral badge rather than being hidden, so a new GitLab status shows
 * up as-is instead of silently reading "no pipeline".
 */
const PIPELINE_STYLE: Record<string, { color: string; icon: string }> = {
  success: { color: "success", icon: "check" },
  failed: { color: "danger", icon: "cross" },
  canceled: { color: "default", icon: "minusInCircle" },
  canceling: { color: "default", icon: "minusInCircle" },
  skipped: { color: "default", icon: "sortRight" },
  running: { color: "primary", icon: "play" },
  pending: { color: "warning", icon: "clock" },
  created: { color: "hollow", icon: "clock" },
  preparing: { color: "hollow", icon: "clock" },
  waiting_for_resource: { color: "warning", icon: "clock" },
  manual: { color: "warning", icon: "hand" },
  scheduled: { color: "warning", icon: "calendar" },
};

/** True for statuses that mean "someone has to look at this". */
export const isPipelineBad = (status?: string | null): boolean =>
  status === "failed" || status === "canceled";

/**
 * The latest pipeline of one ref as a linked badge. Renders a muted placeholder
 * when the ref has no pipeline at all - a tag pushed before CI existed, or a
 * branch that never ran.
 */
export const PipelineBadge = ({
  pipeline,
}: {
  pipeline: OpsanioRefPipeline | null;
}): JSX.Element => {
  const { translate } = useAnketaI18n();

  if (!pipeline) {
    return (
      <EuiText size="xs" color="subdued">
        {translate(`${I18N}.pipeline.none`, { defaultMessage: "no pipeline" })}
      </EuiText>
    );
  }

  const style = PIPELINE_STYLE[pipeline.status] ?? {
    color: "hollow",
    icon: "questionInCircle",
  };

  return (
    <EuiToolTip
      content={translate(`${I18N}.pipeline.tooltip`, {
        defaultMessage:
          "Pipeline #{{id}} ({{source}}) auf {{ref}} - {{status}}, {{updated}}",
        values: {
          id: String(pipeline.id),
          source: pipeline.source,
          ref: pipeline.ref,
          status: pipeline.status,
          updated: new Date(pipeline.updated_at).toLocaleString(),
        },
      })}
    >
      <EuiLink href={pipeline.web_url} target="_blank" external={false}>
        <EuiBadge color={style.color} iconType={style.icon}>
          {pipeline.status}
        </EuiBadge>
      </EuiLink>
    </EuiToolTip>
  );
};

/** Small "n commits / tag" glyph used next to a resolved ref. */
export const RefIcon = ({ type }: { type: "branch" | "tag" }): JSX.Element => (
  <EuiIcon type={type === "branch" ? "branch" : "tag"} size="s" />
);
